import { Box, Typography } from '@mui/material';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { useDispatch, useSelector } from 'react-redux';

import { Button } from '@/components';
import { Modals, TrackerMessages } from '@/constants';
import { AnalyticsService, EVENTS } from '@/services';
import {
  selectActiveModalParams,
  uiActions,
  useDeleteCustomCertificateMutation,
} from '@/store';
import { Certificate } from '@/types';
import { SnackbarUtils } from '@/utils';

import { AppModal } from '../AppModal';

export const TrackerDeleteModal = () => {
  const dispatch = useDispatch();

  const { credit } =
    (useSelector(selectActiveModalParams) as { credit: Certificate }) || {};

  const [deleteCustomCertificate, { isLoading: isDeleteLoading }] =
    useDeleteCustomCertificateMutation();

  const onClose = () => dispatch(uiActions.closeActiveModal());

  const onDelete = async () => {
    try {
      AnalyticsService.logEvent(
        EVENTS.TRACKER_EVENTS.DELETE_CUSTOM_CERTIFICATE.INTENT,
        {
          certificateId: credit.certificateId,
        },
      );
      await deleteCustomCertificate({
        certificateId: credit.certificateId,
      }).unwrap();
      AnalyticsService.logEvent(
        EVENTS.TRACKER_EVENTS.DELETE_CUSTOM_CERTIFICATE.SUCCESS,
      );
      onClose();
      SnackbarUtils.success(TrackerMessages.DELETE_CUSTOM_CPD_SUCCESS);
    } catch (error) {
      const requestError = error as FetchBaseQueryError;
      AnalyticsService.logEvent(
        EVENTS.TRACKER_EVENTS.DELETE_CUSTOM_CERTIFICATE.FAIL,
        {
          reason: (requestError?.data as { message: string })?.message || '',
        },
      );
      SnackbarUtils.error(TrackerMessages.DELETE_CUSTOM_CPD_ERROR);
    }
  };

  if (!credit) return null;

  return (
    <AppModal modalName={Modals.TRACKER_DELETE} scroll="body">
      <Box
        sx={{
          p: 3,
          textAlign: 'center',
        }}>
        <Typography sx={{ mb: 3 }}>
          Are you sure you would like to delete this record?
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
          }}>
          <Box sx={{ mr: 3 }}>
            <Button
              variant="tertiary"
              disabled={isDeleteLoading}
              onClick={onClose}
              label="No"
              fullWidth={false}
            />
          </Box>
          <Button
            variant="gradientPrimary"
            disabled={isDeleteLoading}
            onClick={onDelete}
            label="Yes"
            fullWidth={false}
          />
        </Box>
      </Box>
    </AppModal>
  );
};
